import { useState, type ReactNode } from 'react';
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  ClipboardCheck,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import type { ActionGroup, DashboardSlice } from '@/api/schema';
import { visibleNavigation } from '@/app/navigation';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Chip, type ChipTone } from '@/components/ui/Chip';
import { ErrorState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { labelledBy, SOURCE_LABEL, STATUS_LABEL } from '@/features/candidates/labels';
import { useActionsDue } from '@/features/notifications/api';
import { formatDate, todayIso } from '@/lib/dates';

import { useDashboard } from '../api';
import { Area, Bars } from '../components/Charts';
import styles from './DashboardPage.module.css';

const RANGES = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last year' },
  { value: '1825', label: 'Last 5 years' },
];

const PRIORITY: Record<ActionGroup['urgency'], { label: string; tone: ChipTone }> = {
  overdue: { label: 'Overdue', tone: 'rejected' },
  today: { label: 'Today', tone: 'applicant-arranged' },
  follow_up: { label: 'Follow up', tone: 'neutral' },
};

function isoDaysAgo(days: number): string {
  const at = new Date();
  at.setDate(at.getDate() - days);
  return formatDate(at, 'YYYY-MM-DD');
}

function Metric({
  label,
  value,
  detail,
  to,
}: {
  label: string;
  value: number;
  detail: string;
  to?: string | undefined;
}) {
  return (
    <div className={styles.metric}>
      <span className={styles.metricLabel}>{label}</span>
      <strong className={styles.metricValue}>{value.toLocaleString()}</strong>
      <span className={styles.muted}>{detail}</span>
      {to ? (
        <Link className={styles.metricLink} to={to} aria-label={`Open ${label}`}>
          Open
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  );
}

function RowLink({ to, title, children }: { to: string; title: string; children: ReactNode }) {
  return (
    <Link to={to} className={styles.rowLink}>
      <span>{title}</span>
      {children}
      <ArrowRight size={15} aria-hidden="true" />
    </Link>
  );
}

export function DashboardPage() {
  const [days, setDays] = useState('90');
  const from = isoDaysAgo(Number(days) - 1);
  const to = todayIso();
  const dashboard = useDashboard(from, to);
  const actions = useActionsDue();
  const { data: user } = useCurrentUser();
  const allowed = new Set(
    visibleNavigation(user).flatMap(({ sections }) =>
      sections.flatMap(({ items }) => items.map(({ path }) => path)),
    ),
  );
  const groups = (actions.data?.groups ?? []).filter(({ route }) =>
    allowed.has(route.split('?')[0] ?? ''),
  );
  const data = dashboard.data;
  const candidateUrl = (extra?: Record<string, string>) =>
    `/candidates?${new URLSearchParams({ received_from: from, received_to: to, ...extra })}`;
  const breakdown = (rows: DashboardSlice[], labels: Record<string, string>, filter: string) =>
    rows.map(({ label, count }) => ({
      label: labelledBy(labels)(label),
      count,
      ...(allowed.has('/candidates') ? { to: candidateUrl({ [filter]: label }) } : {}),
    }));

  const previous = data?.summary.previous_total;
  const change = previous == null || !data ? null : data.summary.total - previous;
  const comparison =
    change === null
      ? 'Received in this period'
      : `${change === 0 ? 'No change' : `${change > 0 ? '+' : ''}${change.toLocaleString()}`} vs previous ${days} days`;

  return (
    <Page className={styles.page}>
      <PageHeader
        className={styles.header}
        title="Recruitment dashboard"
        description="Prioritise follow-ups and track how candidates and applications are moving through the CRM."
        actions={
          <Select
            aria-label="Date range"
            value={days}
            onChange={(event) => {
              setDays(event.target.value);
            }}
            options={RANGES}
          />
        }
      />

      <Panel
        title="Needs attention"
        icon={<ClipboardCheck size={17} aria-hidden="true" />}
        aside="Current workload"
      >
        {actions.isError ? (
          <ErrorState
            message="The action queue did not load."
            onRetry={() => {
              void actions.refetch();
            }}
          />
        ) : actions.isPending ? (
          <Spinner label="Loading actions" />
        ) : groups.length === 0 ? (
          <p className={styles.muted}>No follow-ups are due in your accessible workspaces.</p>
        ) : (
          <ul className={styles.actionGrid}>
            {groups.map(({ key, title, urgency, count, route }) => (
              <li key={key}>
                <RowLink to={route} title={title}>
                  <Chip tone={PRIORITY[urgency].tone} size="sm">
                    {PRIORITY[urgency].label}
                  </Chip>
                  <strong>{count}</strong>
                </RowLink>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {dashboard.isError ? (
        <ErrorState
          message="Recruitment analytics did not load."
          onRetry={() => {
            void dashboard.refetch();
          }}
        />
      ) : dashboard.isPlaceholderData || !data ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading recruitment analytics" />
        </div>
      ) : (
        <>
          <div className={styles.metrics}>
            <Metric
              label="Candidates received"
              value={data.summary.total}
              detail={comparison}
              to={allowed.has('/candidates') ? candidateUrl() : undefined}
            />
            <Metric
              label="In screening"
              value={data.summary.in_screening}
              detail="From candidates received in this period"
            />
            <Metric
              label="In the ATS"
              value={data.summary.in_ats}
              detail="From candidates received in this period"
            />
            <Metric
              label="Upcoming meetings"
              value={data.summary.meetings_upcoming}
              detail="Scheduled from now"
              to={allowed.has('/meetings/calendar') ? '/meetings/calendar' : undefined}
            />
          </div>

          <div className={styles.grid}>
            <Panel
              title="Candidate intake by week"
              icon={<CalendarDays size={17} aria-hidden="true" />}
            >
              <Area points={data.weekly} empty="No candidates arrived in this period." />
            </Panel>

            {data.careers ? (
              <Panel
                title="Vacancy responses"
                icon={<BriefcaseBusiness size={17} aria-hidden="true" />}
              >
                <dl className={styles.careersTotals}>
                  <div>
                    <dt>Open vacancies now</dt>
                    <dd>
                      <Link to="/careers">{data.careers.open_vacancies}</Link>
                    </dd>
                  </div>
                  <div>
                    <dt>Website applications this period</dt>
                    <dd>
                      <Link to="/careers/applications">
                        {data.careers.applications_received}
                      </Link>
                    </dd>
                  </div>
                </dl>
                {data.careers.top_vacancies.length > 0 ? (
                  <>
                    <h3 className={styles.subheading}>Jobs with the most applications</h3>
                    <ul className={styles.vacancies}>
                      {data.careers.top_vacancies.map(
                        ({ vacancy_id, title, country, count }) => (
                          <li key={vacancy_id}>
                            <RowLink
                              to={`/careers/applications?vacancy=${vacancy_id}`}
                              title={`${title} · ${country}`}
                            >
                              <strong>{count}</strong>
                            </RowLink>
                          </li>
                        ),
                      )}
                    </ul>
                  </>
                ) : (
                  <p className={styles.muted}>No website applications in this period.</p>
                )}
              </Panel>
            ) : null}

            <Panel
              title="Current candidate status"
              icon={<Users size={17} aria-hidden="true" />}
            >
              <Bars
                rows={breakdown(data.by_status, STATUS_LABEL, 'status')}
                empty="No candidates in this period."
              />
            </Panel>
            <Panel title="Candidate sources">
              <Bars
                rows={breakdown(data.by_source, SOURCE_LABEL, 'source')}
                empty="No candidates in this period."
              />
            </Panel>
            <Panel title="Candidates by sector">
              <Bars rows={data.by_department} empty="No candidates in this period." />
            </Panel>
            <Panel title="Preferred destinations">
              <Bars rows={data.by_destination} empty="No candidates in this period." />
            </Panel>
          </div>
        </>
      )}
    </Page>
  );
}
